import { useMemo, useState } from 'react'
import { Alert, Spinner } from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'

import { GET_MESSAGE_TEMPLATES } from '../../gql/queries/campaigns'
import {
  CREATE_MESSAGE_TEMPLATE,
  UPDATE_MESSAGE_TEMPLATE,
  DUPLICATE_MESSAGE_TEMPLATE,
  TOGGLE_TEMPLATE_BLUEPRINT,
} from '../../gql/mutations/campaigns'
import { IMessageTemplate, IMessageTemplatesRes, CampaignChannel } from '../../types/campaigns'
import { TemplatesPanel } from '../campaigns/components/TemplatesPanel'
import { TemplateFormModal } from '../campaigns/components/TemplateFormModal'
import { TemplateStarterModal } from './components/TemplateStarterModal'
import { TemplateStarter } from '../../constants/templateStarters'
import { ConfirmationModal } from '../../components/ConfirmationModal'

export const TemplatesPage = () => {
  const [starterModalOpen, setStarterModalOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<IMessageTemplate | null>(null)
  const [defaultChannel, setDefaultChannel] = useState<CampaignChannel | undefined>(undefined)
  const [selectedStarter, setSelectedStarter] = useState<TemplateStarter | null>(null)

  // Duplicate modal state
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false)
  const [templateToDuplicate, setTemplateToDuplicate] = useState<IMessageTemplate | null>(null)

  const {
    data,
    loading,
    error,
    refetch,
  } = useQuery<IMessageTemplatesRes>(GET_MESSAGE_TEMPLATES, {
    fetchPolicy: 'cache-and-network',
  })

  const templates = useMemo(() => data?.messageTemplates ?? [], [data])

  const [createTemplateMutation, { loading: creating }] = useMutation(CREATE_MESSAGE_TEMPLATE)
  const [updateTemplateMutation, { loading: updating }] = useMutation(UPDATE_MESSAGE_TEMPLATE)
  const [duplicateTemplateMutation, { loading: duplicating }] = useMutation(DUPLICATE_MESSAGE_TEMPLATE)
  const [toggleBlueprintMutation] = useMutation(TOGGLE_TEMPLATE_BLUEPRINT)

  const handleClose = () => {
    setModalOpen(false)
    setEditingTemplate(null)
    setDefaultChannel(undefined)
    setSelectedStarter(null)
  }

  const handleOpenCreate = (channel?: CampaignChannel) => {
    setEditingTemplate(null)
    setDefaultChannel(channel)
    setStarterModalOpen(true)
  }

  const handleSelectStarter = (starter: TemplateStarter) => {
    setSelectedStarter(starter)
    setDefaultChannel(starter.channel)
    setStarterModalOpen(false)
    setModalOpen(true)
  }

  const handleStartFromScratch = () => {
    setSelectedStarter(null)
    setStarterModalOpen(false)
    setModalOpen(true)
  }

  const handleEditTemplate = (template: IMessageTemplate) => {
    setEditingTemplate(template)
    setDefaultChannel(template.channel)
    setModalOpen(true)
  }

  const handleSubmit = async (payload: {
    name: string
    description?: string
    channel: CampaignChannel
    subject?: string
    body: string
    variables?: string[]
    metadata?: Record<string, any>
    enabledChannels?: CampaignChannel[]
  }) => {
    try {
      if (editingTemplate) {
        await updateTemplateMutation({
          variables: {
            id: editingTemplate.id,
            ...payload,
          },
        })
        toast.success('Template updated')
      } else {
        await createTemplateMutation({
          variables: payload,
        })
        toast.success('Template created')
      }
      handleClose()
      await refetch()
    } catch (error) {
      toast.error('Unable to save template')
    }
  }

  const handleDuplicateClick = (template: IMessageTemplate) => {
    setTemplateToDuplicate(template)
    setDuplicateModalOpen(true)
  }

  const handleConfirmDuplicate = async () => {
    if (!templateToDuplicate) return

    const newName = `${templateToDuplicate.name} (Copy)`
    try {
      await duplicateTemplateMutation({
        variables: {
          id: templateToDuplicate.id,
          newName
        }
      })
      toast.success('Template duplicated')
      await refetch()
      setDuplicateModalOpen(false)
      setTemplateToDuplicate(null)
    } catch (e) {
      toast.error('Could not duplicate template')
    }
  }

  const handleToggleBlueprint = async (template: IMessageTemplate, isBlueprint: boolean) => {
    try {
      await toggleBlueprintMutation({
        variables: { id: template.id, isBlueprint },
      })
      toast.success(isBlueprint ? '⭐ Template marked as blueprint' : 'Blueprint status removed')
      await refetch()
    } catch (e) {
      toast.error('Could not update blueprint status')
    }
  }

  return (
    <div className='templates-page'>
      <div className='d-flex align-items-center justify-content-between mb-6 flex-wrap gap-3'>
        <div>
          <h1 className='page-title'>Messaging templates</h1>
          <div className='text-muted'>
            Centralize SMS, email, WhatsApp and Telegram content.
            Create multi-channel variants for each template.
          </div>
        </div>
        {loading && <Spinner animation='border' size='sm' />}
      </div>
      {error && (
        <Alert variant='danger'>
          Unable to load templates. {error.message}
        </Alert>
      )}
      <TemplatesPanel
        templates={templates}
        onCreate={() => handleOpenCreate()}
        onEdit={handleEditTemplate}
        onDuplicate={handleDuplicateClick}
        onToggleBlueprint={handleToggleBlueprint}
      />
      <TemplateStarterModal
        show={starterModalOpen}
        onHide={() => setStarterModalOpen(false)}
        onSelect={handleSelectStarter}
        onStartFromScratch={handleStartFromScratch}
      />
      <TemplateFormModal
        show={modalOpen}
        onHide={handleClose}
        onSubmit={handleSubmit}
        template={editingTemplate}
        starter={selectedStarter}
        loading={creating || updating}
        defaultChannel={defaultChannel}
      />

      {/* Duplicate Confirmation Modal */}
      <ConfirmationModal
        show={duplicateModalOpen}
        onHide={() => setDuplicateModalOpen(false)}
        onConfirm={handleConfirmDuplicate}
        title="Duplicate Template"
        message={
          templateToDuplicate
            ? `Are you sure you want to duplicate "${templateToDuplicate.name}" as "${templateToDuplicate.name} (Copy)"?`
            : "Are you sure you want to duplicate this template?"
        }
        confirmText="Duplicate"
        loading={duplicating}
      />
    </div>
  )
}
